import { Button, Input, Textarea } from "@mantine/core";
import RoutesInstance from "@routes/instances";
import { useMutation } from "@tanstack/react-query";
import Common from "@utilities/common";
import { Form, Formik, FormikHelpers } from "formik";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IFormData } from "./types";
import { Schema } from "./validations";
import Editor from "@components/editor";

interface Props {
    data: IBrand["get"];
}

const FormContainer: FC<Props> = ({ data }) => {
    const [initialValues, setInitialValues] = useState<IFormData>({
        enName: "",
        faName: "",
        url: "",
        description: "",
        seoDescription: "",
        seoKeywords: "",
    });

    const Navigate = useNavigate();

    const { mutate, isLoading } = useMutation(
        ({
            faName,
            enName,
            url,
            description,
            seoDescription,
            seoKeywords,
        }: IFormData) =>
            Common.api.brands.Edit({
                id: data.id,
                faName,
                enName,
                url,
                ...(description ? { description } : {}),
                ...(seoDescription ? { seoDescription } : {}),
                ...(seoKeywords ? { seoKeywords } : {}),
            }),
        {
            onSuccess: () => {
                Navigate(RoutesInstance.brands);
            },
        },
    );

    useEffect(() => {
        setInitialValues({
            faName: data.faName,
            enName: data.enName,
            url: data.url,
            description: data?.description || "",
            seoDescription: data?.seoDescription || "",
            seoKeywords: data?.seoKeywords || "",
        });
    }, [data]);

    const handleSubmit = async (
        values: IFormData,
        formik: FormikHelpers<IFormData>,
    ) => {
        mutate(values);
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={Schema}
            validateOnBlur={true}
            validateOnChange={true}
            enableReinitialize>
            {({ values, errors, handleChange, handleBlur, isValid }) => (
                <Form>
                    <div className="mb-4">
                        <Input.Wrapper
                            id="faName"
                            label="نام فارسی"
                            required
                            error={errors.faName}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="faName"
                                name="faName"
                                type="text"
                                placeholder="نام فارسی"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.faName}
                                invalid={!!errors.faName}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <Input.Wrapper
                            id="enName"
                            label="نام انگلیسی"
                            required
                            error={errors.enName}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="enName"
                                name="enName"
                                type="text"
                                placeholder="نام انگلیسی"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.enName}
                                invalid={!!errors.enName}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <Input.Wrapper
                            id="url"
                            label="آدرس (انگلیسی - بدون فاصله همراه با'-')"
                            required
                            error={errors.url}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="url"
                                name="url"
                                type="text"
                                placeholder="آدرس (انگلیسی - بدون فاصله همراه با'-')"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.url}
                                invalid={!!errors.url}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <Editor
                            value={values.description}
                            handleChange={(value: string) =>
                                handleChange({
                                    target: {
                                        id: "description",
                                        name: "description",
                                        value,
                                    },
                                })
                            }
                        />
                    </div>

                    <div className="mb-4">
                        <Textarea
                            variant="filled"
                            color="blue"
                            id="seoDescription"
                            name="seoDescription"
                            placeholder="توضیحات سئو"
                            label="توضیحات سئو"
                            minRows={10}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isLoading}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                            error={errors.seoDescription}
                            value={values.seoDescription}
                        />
                    </div>

                    <div className="mb-10">
                        <Input.Wrapper
                            id="seoKeywords"
                            label="کلمات کلیدی سئو"
                            error={errors.seoKeywords}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="seoKeywords"
                                name="seoKeywords"
                                type="text"
                                placeholder="کلمات کلیدی سئو"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.seoKeywords}
                                invalid={!!errors.seoKeywords}
                            />
                        </Input.Wrapper>
                    </div>
                    <div className="text-center">
                        <Button
                            variant="filled"
                            className="transition-all duration-300 px-16"
                            color="blue"
                            radius={9999}
                            type="submit"
                            disabled={!isValid || isLoading}
                            loading={isLoading}>
                            ثبت
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default FormContainer;
